import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { Box, Typography, Button, Tab } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUserActions } from "../../hooks/useUserActions.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import AdminUserForm from "./components/AdminUserForm.jsx";
import ContractAssignmentPanel from "./components/ContractAssignmentPanel.jsx";

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const { createOrUpdateUser } = useUserActions();

  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState("1");

  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    try {
      await backendGet(`data/user/${userId}`, {
        label: "Benutzerdaten",
        enqueueSnackbar,
        setResult: (res) => {
          setUserData(res?.data);
        },
      });
    } catch (error) {
      enqueueSnackbar("Benutzerdaten konnten nicht geladen werden.", {
        variant: "error",
      });
      console.error("Failed to fetch user data", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, enqueueSnackbar]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Ensure user_display_name is always calculated from the most recent user_data
  const user_display_name =
    userData?.full_name || userData?.email || `Benutzer #${userId}`;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Benutzer", path: "/admin/users" },
      { name: user_display_name, path: `/admin/users/${userId}` },
    ]);
  }, [setBreadcrumbs, user_display_name, userId]);

  const handleTabChange = (_event, new_value) => {
    setTabValue(new_value);
  };

  const handleSubmit = useCallback(
    async (formData, formMethods) => {
      try {
        await createOrUpdateUser(formData, formMethods?.setError);
        enqueueSnackbar("Änderungen gespeichert.", {
          variant: "success",
        });
        await fetchUser();
        return true;
      } catch {
        // Validation errors (e.g. Email bereits vergeben) wurden bereits
        // per setError am betroffenen Feld angezeigt; Formular bleibt erhalten.
        return false;
      }
    },
    [createOrUpdateUser, enqueueSnackbar, fetchUser]
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!userData) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          Benutzerdetails
        </Typography>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Benutzer nicht gefunden
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
          size="small"
        >
          Benutzer
        </Button>
      </Box>

      <Typography variant="h1" gutterBottom>
        {user_display_name}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {userData.email} – {userData.displayname_rolle || userData.rolle}
      </Typography>

      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <TabList onChange={handleTabChange}>
            <Tab label="Benutzerdaten" value="1" />
            <Tab label="Vertragszuordnungen" value="2" />
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ px: 0 }}>
          <AdminUserForm
            initialData={userData}
            isEditMode={true}
            onSubmit={handleSubmit}
          />
        </TabPanel>

        <TabPanel value="2" sx={{ px: 0 }}>
          <ContractAssignmentPanel userId={parseInt(userId, 10)} />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
